import { isEmpty } from 'lodash-es';
import { Aggregations } from '../enums';
import { isFertigationMetric, isFrost, isHydricStress } from '../enums/MetricIds';
import TimeSteps from '../enums/TimeSteps';
import { isDeviceSummary, isPlotSummary } from '../resources/measurements.type';
import { keysOf } from '../utils';
export var Universe;
(function (Universe) {
    Universe[Universe["overview"] = -1] = "overview";
    Universe[Universe["weather"] = 0] = "weather";
    Universe[Universe["irrigation"] = 1] = "irrigation";
    Universe[Universe["frost"] = 2] = "frost";
    Universe[Universe["irrelis"] = 3] = "irrelis";
})(Universe || (Universe = {}));
/** List of all universes names */
export const universes = [
    Universe.overview,
    Universe.weather,
    Universe.irrigation,
    Universe.frost,
    Universe.irrelis
];
/** List of all universe aggregates */
const allUniverseAggregates = [
    'real-time-PPFD_MAX',
    'real-time-PPFD_MIN',
    'real-time-SSI_MAX',
    'real-time-SSI_MIN',
    'real-time-FF',
    'real-time-FXY',
    'real-time-DD',
    'real-time',
    'min-measured-today',
    'max-measured-today',
    'cumul-measured-today',
    'cumul-measured-today+yesterday',
    'cumul-measured-7days',
    'cumul-measured-12H',
    'cumul-measured-4H',
    'n-1-measure',
    'n-2-measure',
    'd-0-measure',
    'd-1-measure',
    'd-3-measure',
    'd-7-measure',
    // Not really a measure if its in the future though
    'd+1-measure',
    'd+3-measure',
    'd+5-measure'
];
/** Shared between {@link Universe.weather} & {@link Universe.frost}, only real times */
const windAggregates = ['real-time-FF', 'real-time-FXY', 'real-time-DD'];
/** {@link Universe.frost} only aggregates, n-{x} is the nth (n) previous measure */
const frostAggregates = ['real-time', 'n-1-measure', 'n-2-measure'];
/** Real time, min & max measured today aggregates */
const realTimeExtremesAggregates = [
    'real-time',
    'min-measured-today',
    'max-measured-today'
];
/** Solar radiation in real time and max aggregates */
const realTimeSolarRadiation = [
    'real-time',
    'real-time-SSI_MIN',
    'real-time-SSI_MAX'
];
/** PPFD in real time and max aggregates */
const realTimePhotosyntheticPhotonFlux = [
    'real-time',
    'real-time-PPFD_MIN',
    'real-time-PPFD_MAX'
];
/** Rainfalls specific aggregates */
const rainfallsAggregates = [
    'cumul-measured-today',
    'cumul-measured-today+yesterday',
    'cumul-measured-7days'
];
/** {@link Universe.irrigation} only aggregates, d-{x} is the nth day (d) previous measure */
const irrigationAggregates = [
    'd-7-measure',
    'd-1-measure',
    'd-0-measure',
    'real-time'
];
/** {@link Universe.irrelis} only aggregates, d+{x} are forecast from Irré-LIS */
const irrelisAggregates = [
    'd-0-measure',
    'd+1-measure',
    'd+3-measure',
    'd+5-measure'
];
/** {@link Universe.weather} */
const leafWetnessAggregates = [
    'real-time',
    'cumul-measured-4H',
    'cumul-measured-12H'
];
export const allUniverseMetrics = [
    'HDEF',
    'HPOT',
    'U_CAPA',
    'T',
    'RR',
    'U',
    'FF',
    'T_WET',
    'T_DRY',
    'PPFD',
    'SSI',
    'LW_V',
    'T_SOIL',
    'ECP',
    'SWC'
];
/**
 * Record of aggregate by metric by universe
 */
export const aggByMetricByUniv = {
    [Universe.overview]: {},
    [Universe.weather]: {
        RR: rainfallsAggregates,
        T: realTimeExtremesAggregates,
        U: realTimeExtremesAggregates,
        FF: windAggregates,
        T_SOIL: realTimeExtremesAggregates,
        LW_V: leafWetnessAggregates,
        PPFD: realTimePhotosyntheticPhotonFlux,
        SSI: realTimeSolarRadiation
    },
    [Universe.frost]: {
        T_WET: frostAggregates,
        T_DRY: frostAggregates,
        FF: windAggregates
    },
    [Universe.irrigation]: {
        HPOT: irrigationAggregates,
        U_CAPA: irrigationAggregates,
        RR: rainfallsAggregates,
        ECP: realTimeExtremesAggregates,
        SWC: realTimeExtremesAggregates
    },
    [Universe.irrelis]: {
        HDEF: irrelisAggregates,
        RR: rainfallsAggregates
    }
};
/** Get all available metrics from summary */
export const getMetricsFromSummary = (summary) => {
    let measures = [];
    const resource = isPlotSummary(summary)
        ? summary.metadata.plot
        : isDeviceSummary(summary)
            ? summary.metadata.device
            : summary.metadata.station;
    if ('currentMeasurementConfigs' in resource) {
        const summaryDataKeys = keysOf(summary.data);
        const hasHDEFInKeys = !isEmpty(summaryDataKeys) && summaryDataKeys.some((key) => key.includes('HDEF'));
        measures = resource.currentMeasurementConfigs.map((conf) => conf.metric);
        if (hasHDEFInKeys) {
            measures.push('HDEF');
        }
    }
    else if ('availableMeasures' in resource) {
        measures = resource.availableMeasures;
    }
    return measures;
};
/** Get all available universe for metrics array */
export const getAvailableUniversesFromMetrics = (metrics) => {
    const availableUniverses = new Set([Universe.weather]);
    metrics.map((metric) => {
        if (isFrost(metric))
            availableUniverses.add(Universe.frost);
        if (isHydricStress(metric) || isFertigationMetric(metric))
            availableUniverses.add(Universe.irrigation);
        if (metric === 'HDEF')
            availableUniverses.add(Universe.irrelis);
    });
    return Array.from(availableUniverses);
};
/** Get all available metrics for a universe */
export const getUniverseMetrics = (univ) => {
    return Object.keys(aggByMetricByUniv[univ]);
};
/** Get all available aggregates for a universe and a metric */
export function getUniverseAggs(univ, metric) {
    return aggByMetricByUniv[univ][metric];
}
/** Mapping from a universe aggregate to its metric aggregate */
export const univAggToAggregation = {
    'real-time-SSI_MAX': Aggregations.latest,
    'real-time-SSI_MIN': Aggregations.latest,
    'real-time-PPFD_MIN': Aggregations.latest,
    'real-time-PPFD_MAX': Aggregations.latest,
    'real-time-FF': Aggregations.latest,
    'real-time-FXY': Aggregations.latest,
    'real-time-DD': Aggregations.latest,
    'real-time': Aggregations.latest,
    'min-measured-today': Aggregations.minimum,
    'max-measured-today': Aggregations.maximum,
    'cumul-measured-today': Aggregations.today,
    'cumul-measured-today+yesterday': Aggregations.yesterday,
    'cumul-measured-7days': Aggregations.week,
    'n-1-measure': Aggregations.lastValues,
    'n-2-measure': Aggregations.lastValues,
    'd-0-measure': Aggregations.today,
    'd-1-measure': Aggregations.yesterday,
    'd-3-measure': Aggregations.threeDaysAgo,
    'd-7-measure': Aggregations.week,
    'd+1-measure': Aggregations.yesterday,
    'd+3-measure': Aggregations.threeDaysAgo,
    'd+5-measure': Aggregations.fiveDaysAgo,
    'cumul-measured-12H': Aggregations.lastTwelveHours,
    'cumul-measured-4H': Aggregations.lastFourHours
};
/** Mapping from a universe aggregate to its time step */
export const univAggToTimeStep = {
    'real-time-SSI_MAX': TimeSteps.maximum,
    'real-time-SSI_MIN': TimeSteps.maximum,
    'real-time-PPFD_MIN': TimeSteps.maximum,
    'real-time-PPFD_MAX': TimeSteps.maximum,
    'real-time-FF': TimeSteps.maximum,
    'real-time-FXY': TimeSteps.maximum,
    'real-time-DD': TimeSteps.maximum,
    'real-time': TimeSteps.maximum,
    'min-measured-today': TimeSteps.perDays,
    'max-measured-today': TimeSteps.perDays,
    'cumul-measured-today': TimeSteps.perDays,
    'cumul-measured-today+yesterday': TimeSteps.perDays,
    'cumul-measured-7days': TimeSteps.perWeek,
    'n-1-measure': TimeSteps.maximum,
    'n-2-measure': TimeSteps.maximum,
    'd-0-measure': TimeSteps.perDays,
    'd-1-measure': TimeSteps.perDays,
    'd-7-measure': TimeSteps.perDays,
    'd-3-measure': TimeSteps.perDays,
    'd+1-measure': TimeSteps.perDays,
    'd+3-measure': TimeSteps.perDays,
    'd+5-measure': TimeSteps.perDays,
    'cumul-measured-12H': TimeSteps.perHours,
    'cumul-measured-4H': TimeSteps.perHours
};
export const colorByUniv = {
    [Universe.overview]: {
        backgroundColor: 'grayscale.black',
        borderColor: '#000'
    },
    [Universe.weather]: {
        backgroundColor: 'primary.500',
        borderColor: 'primary.800'
    },
    [Universe.irrigation]: {
        backgroundColor: 'metrics.IRR.500',
        borderColor: 'metrics.IRR.800'
    },
    [Universe.frost]: {
        backgroundColor: 'metrics.T_WET.500',
        borderColor: 'metrics.T_WET.800'
    },
    [Universe.irrelis]: {
        backgroundColor: 'dss.irrelis',
        borderColor: 'grayscale.black'
    }
};
